import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SuggestionResult } from 'core/type/SuggestionResult.types';
import { Geo } from 'core/type/BootstrapConfig.types';
import { RootState } from 'store/store.types';
import { selectGeoFromBootstrap } from 'store/slice/bootstrap';
import { getDeSanitizeName } from 'core/utils/activityUrlGenerator';
import { getTravelDateFromQueryParams } from 'gql/service/graph/activityService';
import { isPast, isToday } from 'date-fns';
import { TopDestination } from 'core/type/ActivitiesTopDestinations.types';

export interface SearchBoxSettings {
    textSearch?: SuggestionResult;
    travelDate?: Date;
}

export interface HomeState {
    searchBoxSettings: SearchBoxSettings;
    topDomesticDestinations: TopDestination[];
}

const initialState: HomeState = {
    searchBoxSettings: {},
    topDomesticDestinations: [],
};

const homeSlice = createSlice({
    name: 'home',
    initialState,
    reducers: {
        setSearchBoxSettingsState: (state, action: PayloadAction<SearchBoxSettings>) => {
            state.searchBoxSettings = action.payload;
        },
        setTextSearchInSearchBoxSettingsState: (state, action: PayloadAction<SuggestionResult | undefined>) => {
            state.searchBoxSettings.textSearch = action.payload;
        },
        setTravelDateInSearchBoxSettingsState: (state, action: PayloadAction<Date>) => {
            if (!isPast(action.payload) || isToday(action.payload)) {
                state.searchBoxSettings.travelDate = action.payload;
            }
        },
        setTravelDateInSearchBoxSettingsStateFromQueryParams: (state, action: PayloadAction<{ locationSearch: string }>) => {
            state.searchBoxSettings.travelDate = getTravelDateFromQueryParams(action.payload.locationSearch);
        },
    },
});

export const convertGeoToSuggestion = (geo: Geo): SuggestionResult => {
    const result: SuggestionResult = { ...geo };
    result.name = geo.nameTranslated || geo.name;
    return result;
};

export const selectHomeState = (state: RootState) => state.home;
export const selectSearchBoxSettingsTextSearch = createSelector(selectHomeState, (state) => state.searchBoxSettings?.textSearch);
export const selectSearchBoxSettingsTravelDate = createSelector(selectHomeState, (state) => state.searchBoxSettings?.travelDate);
export const selectTextSearchCityName = createSelector(selectHomeState, (state) => state.searchBoxSettings?.textSearch?.cityName || '');
export const selectTextSearchName = createSelector(selectHomeState, (state) => state.searchBoxSettings?.textSearch?.name || '');

export const selectTextForTextSearch = createSelector(selectTextSearchName, selectGeoFromBootstrap, (textSearchCityName, geo) => {
    const searchText = textSearchCityName || getDeSanitizeName(geo?.nameTranslated);
    return searchText;
});

export const { setSearchBoxSettingsState, setTextSearchInSearchBoxSettingsState, setTravelDateInSearchBoxSettingsState, setTravelDateInSearchBoxSettingsStateFromQueryParams } =
    homeSlice.actions;
export default homeSlice.reducer;
